import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Nav.css';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider, firestore } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FaDownload, FaUser, FaSun, FaMoon } from 'react-icons/fa';
import ReactGA from 'react-ga4';

const Nav = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [platform, setPlatform] = useState('Web');
  const [isInstalling, setIsInstalling] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  useEffect(() => {
    const detectPlatform = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      return isPWA ? 'PWA' : 'Web';
    };
    setPlatform(detectPlatform());
  }, []);

  const getPlatform = () => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      return 'PWA';
    }
    return 'Website';
  };

  // Function to track a button click event
  const trackEvent = (category, action, label = '') => {
    // Function is depricated
    // ReactGA.event({
    //   category,
    //   action: action + " - " + getPlatform(),
    //   label,
    //   platform: getPlatform(),
    // });
  }; 

  useEffect(() => {
    const checkPWAStatus = () => {
      const isRunningAsPWA = window.matchMedia('(display-mode: standalone)').matches || 
                            window.navigator.standalone || 
                            document.referrer.includes('android-app://');
      setPlatform(isRunningAsPWA ? 'PWA' : 'Web');
      return isRunningAsPWA;
    };

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
      localStorage.removeItem('installDismissed'); // Reset dismissal if prompt reappears
    };

    // Immediate check on component mount
    if (!checkPWAStatus()) {
      window.addEventListener('beforeinstallprompt', handler);
    }

    const installedHandler = () => {
      setDeferredPrompt(null);
      setShowInstallButton(false);
      localStorage.setItem('installDismissed', 'true');
    };

    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback for when prompt isn't immediately available
      alert('For the best experience, please use Chrome or Safari and look for "Add to Home Screen" in the browser menu.');
      return;
    }

    setIsInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      
      if (choice.outcome === 'accepted') {
        trackEvent(`Install Clicked`, `Install Clicked (Nav)`, 'App side menu');
        console.log('User installed the app');
      } else {
        console.log('User dismissed the install prompt');
      }
    } catch (error) {
      console.error('Installation failed:', error);
      alert('Installation failed. Please try again or use Chrome/Safari.');
    } finally {
      setDeferredPrompt(null);
      setShowInstallButton(false);
      setIsInstalling(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setIsLoggedIn(!!user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userRef = doc(firestore, 'users', user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        });
      }

      navigate('/form?login=complete');
    } catch (error) {
      console.error('Error signing in with Google:', error);
      alert('Error signing in with Google');
    }
  };

  const navBackgroundColor = scrollY > 45 ? 'white' : isDarkMode ? '#1a1a1a' : 'black';
  const textColor = scrollY > 45 ? 'black' : 'white';

  return (
    <div
      className='nav-bar'
      style={{
        backgroundColor: navBackgroundColor,
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        boxShadow: scrollY > 10 ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'
      }}
    >
      <Link 
        to="/" 
        className='gradient-text'
        style={{ fontSize: '1.5rem', fontWeight: '600' }}
      >
        VedicRashi
      </Link>
      
      <div className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu}>
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <div 
        id='nav-right' 
        className={`${isMobileMenuOpen ? 'open' : ''}`}
        style={{
          backgroundColor: navBackgroundColor,
          transition: 'background-color 0.3s ease',
        }}
      >
        {/* PWA Install Button - only shown if PWA is installable */}
        

        <Link 
          to="/report/life-report" 
          className="link" 
          style={{ color: textColor }}
        >
          Life Report
        </Link>
        <Link 
          to="/report/varshaphal-report" 
          className="link" 
          style={{ color: textColor }}
        >
          2025 Report
        </Link>
        {/* Modified Install Button with loading state */}
      {showInstallButton && (
        <button 
          onClick={handleInstallClick}
          className='link'
          style={{
            background: 'transparent',
            border: 'none',
            color: textColor,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '1rem',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            opacity: isInstalling ? 0.7 : 1
          }}
          title="Install App"
          disabled={isInstalling}
        >
          {isInstalling ? (
            'Loading...'
          ) : (
            <>
              <i class="ri-mobile-download-line"></i> Install App
            </>
          )}
        </button>
      )}
        <Link 
          className='link' 
          style={{ color: textColor }} 
          to="/about"
        >
          About
        </Link>
        
        <Link 
          to="/pricing" 
          className='link'
          style={{ color: textColor }}
        >
          Pricing
        </Link>
        <Link 
          className='link' 
          style={{ color: textColor }} 
          to="/feedback"
        >
          Feedback
        </Link>

        

        {/* Dark Mode Toggle */}
      

        {isLoggedIn ? (
          <Link 
            to="/profile" 
            className='link'
            style={{
              color: textColor,
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <FaUser /> Profile
          </Link> 
        ) : (
          <Link 
            to="/" 
            className='link'
            style={{ color: textColor }}
            onClick={handleGoogleLogin}
          >
            Login
          </Link>
        )}
      </div>
    </div>
  );
};

export default Nav;